import { fetchDataSet } from 'js/actions/dataSets';
import {
  splitterSetAttr,
  splitterSetExpressions,
  splitterSetFallback,
  splitterSetLoading,
  splitterSetMode,
  splitterSetValues,
} from 'js/actions/splitter';
import useAppDispatch from 'js/hooks/useAppDispatch';
import DataSchemaProcessor from 'js/lib/DataSchemaProcessor';
import FilterDSLParser from 'js/lib/FilterDSLParser';
import { isEmpty } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { MODE_EXPRESSIONS } from './DataSetSplitDialog';
import { ExpressionItem } from './ExpressionsEditor';
import { ValueItem } from './ValuesEditor';

export default function useSplitDataSet(parentDataSet: any | null, splitterDataSet: any | null) {
  const dispatch = useAppDispatch();
  const mode = useSelector((state: any) => state.splitter.mode);
  const fallback = useSelector((state: any) => state.splitter.fallback);
  const expressions = useSelector((state: any) => state.splitter.expressions);
  const attr = useSelector((state: any) => state.splitter.attr);
  const values = useSelector((state: any) => state.splitter.values);
  const isLoading = useSelector((state: any) => state.splitter.isLoading);

  const setMode = (mode: string) => dispatch(splitterSetMode(mode));
  const setFallback = (fallback: string) => dispatch(splitterSetFallback(fallback));
  const setExpressions = (expressions: ExpressionItem[]) =>
    dispatch(splitterSetExpressions(expressions));
  const setAttr = (attr: string | null) => dispatch(splitterSetAttr(attr));
  const setValues = (values: ValueItem[]) => dispatch(splitterSetValues(values));
  const setIsLoading = (loading: boolean) => dispatch(splitterSetLoading(loading));

  const schemaProperties = parentDataSet?.output_schema?.properties;

  // derive flattened schema locally
  const currentParentDataSetSchema = useMemo(() => {
    if (!isEmpty(schemaProperties)) {
      return DataSchemaProcessor.flattenSchema(schemaProperties);
    }
    return [];
  }, [schemaProperties]);

  useEffect(() => {
    // when editing, initialize the state from splitSpec
    const splitSpec = splitterDataSet?.custom_config?.ui?.splitSpec;

    if (splitSpec) {
      const { mode, fallback, expressions, attr, values } = splitSpec;
      setMode(mode);
      setFallback(fallback);

      // set data set IDs corresponding to the expressions/values in the state
      const transformRules = splitterDataSet.transform.transforms?.find(
        ({ operation }) => operation === 'nexla.splitter',
      )?.spec?.rules;
      const setDataSetId = (expression: ExpressionItem | ValueItem, index: number) => ({
        ...expression,
        dataSetId: transformRules?.[index]?.destination?.id,
      });

      if (mode === MODE_EXPRESSIONS) {
        setExpressions(expressions?.map(setDataSetId));
      } else {
        setValues(values?.map(setDataSetId));
      }

      setAttr(attr);
    }
  }, [splitterDataSet]);

  const fetchDataSets = () => {
    if (!parentDataSet?.custom_config || !splitterDataSet?.custom_config) {
      const loaders = [];

      if (parentDataSet && !parentDataSet.custom_config) {
        loaders.push(dispatch(fetchDataSet(parentDataSet.id)));
      }

      if (splitterDataSet && !splitterDataSet.custom_config) {
        loaders.push(dispatch(fetchDataSet(splitterDataSet.id)));
      }

      if (loaders.length > 0) {
        setIsLoading(true);

        Promise.all(loaders).finally(() => {
          setIsLoading(false);
        });
      }
    }
  };

  const validateExpressions = () => {
    if (mode !== MODE_EXPRESSIONS) {
      return null;
    }

    const errors = expressions.reduce((acc, { code }) => {
      try {
        FilterDSLParser.parse(code);
      } catch (error) {
        acc[code] = error.message;
      }

      return acc;
    }, {});

    return isEmpty(errors) ? null : errors;
  };

  return {
    mode,
    fallback,
    expressions,
    attr,
    values,
    parentDataSetSchema: currentParentDataSetSchema,
    isLoading,
    setMode,
    setAttr,
    setValues,
    setExpressions,
    setFallback,
    fetchDataSets,
    validateExpressions,
  };
}
